import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {WorkstreamNarrativeUrlAdminResponse} from 'client';
import {NarrativeUrlsService} from 'client';
import {getService} from 'utilities';
import type {WorkstreamTimeframe} from 'views/workstreams/detail/workstreamTimeframe';
import {NAV_TREE_QUERY_PREFIX} from 'views/workstreams/page/useNavTree';

const queryKey = (timeframe: WorkstreamTimeframe) => ['admin', 'narrative-urls', timeframe];
const publicPillarConfigQueryKey = ['workstream-data', 'pillar-configs'];

export function useAdminNarrativeUrls(
  timeframe: WorkstreamTimeframe,
  options?: {enabled?: boolean}
) {
  return useQuery({
    queryKey: queryKey(timeframe),
    queryFn: () =>
      getService(NarrativeUrlsService).listAdminNarrativeUrlsApiV1AdminWorkstreamNarrativeUrlsGet(
        timeframe
      ),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

export function useUpdateAdminNarrativeUrl(timeframe: WorkstreamTimeframe) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workstreamId,
      narrativeUrl,
    }: {
      workstreamId: number;
      narrativeUrl: string | null;
    }) =>
      getService(
        NarrativeUrlsService
      ).updateAdminNarrativeUrlApiV1AdminWorkstreamWorkstreamIdNarrativeUrlPut(
        workstreamId,
        {narrative_url: narrativeUrl},
        timeframe
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData<WorkstreamNarrativeUrlAdminResponse[]>(
        queryKey(timeframe),
        (current) => {
          if (!current) return [updated];
          // A workstream picked on the page joins the list on its first save.
          return current.some((row) => row.workstream_id === updated.workstream_id)
            ? current.map((row) => (row.workstream_id === updated.workstream_id ? updated : row))
            : [...current, updated];
        }
      );
      await Promise.all([
        queryClient.invalidateQueries({queryKey: publicPillarConfigQueryKey}),
        queryClient.invalidateQueries({queryKey: NAV_TREE_QUERY_PREFIX}),
      ]);
    },
  });
}
